import React from "react";
import { Button } from "@/components/ui/Button";
import { Loader2, FileText } from "lucide-react";
import CloseIcon from "@/components/icons/CloseIcon";

const CONFIRM_TITLES = {
  generate: "Generate Report",
  view: "View report",
  download: "Download report",
};

const PROCESSING_TITLES = {
  generate: "Generating report",
  view: "Opening report",
  download: "Preparing download",
};

const PRIMARY_LABELS = {
  generate: "Generate",
  view: "View",
  download: "Download",
};

/**
 * Shared dialog for generating or opening a report document from the latest reviewed file
 * merged with current edited values (admin / report values).
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {'confirm' | 'processing'} props.phase
 * @param {string} props.reportName
 * @param {'generate' | 'view' | 'download'} [props.intent]
 * @param {() => void} props.onClose — blocked while phase is processing
 * @param {() => void} props.onConfirm
 */
const GenerateReportDialog = ({
  isOpen,
  phase,
  reportName,
  intent = "generate",
  onClose,
  onConfirm,
}) => {
  const safeIntent = PRIMARY_LABELS[intent] ? intent : "generate";
  const name = reportName?.trim() || "this report";

  return (
    <div
      className={`fixed inset-0 z-50 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? "opacity-100" : "pointer-events-none opacity-0"}`}
    >
      <div
        className={`fixed inset-0 bg-white bg-opacity-50 transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
        aria-hidden={!isOpen}
      />
      <div className="fixed inset-0 flex items-center justify-center overflow-y-auto px-4 py-8 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div
          className={`relative z-50 w-full max-w-lg transform rounded-2xl bg-navy-500 p-6 shadow-2xl transition-all duration-300 ${isOpen ? "scale-100 opacity-100" : "scale-95 opacity-0"}`}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="generate-report-dialog-title"
        >
          {phase !== "processing" && (
            <CloseIcon
              className="absolute right-2 top-2 h-8 w-8 cursor-pointer text-red-500 transition-transform duration-200 hover:scale-110"
              onClick={onClose}
              aria-label="Close"
            />
          )}

          <div className="mb-4 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              {phase === "processing" ? (
                <Loader2 className="h-8 w-8 animate-spin text-red-600" />
              ) : (
                <FileText className="h-8 w-8 text-red-600" />
              )}
            </div>
          </div>

          <div className="mb-6 text-center text-gray-0">
            <h3 id="generate-report-dialog-title" className="mb-2 text-xl font-semibold uppercase">
              {phase === "processing"
                ? PROCESSING_TITLES[safeIntent]
                : CONFIRM_TITLES[safeIntent]}
            </h3>
            {phase === "processing" ? (
              <>
                <p className="leading-relaxed">
                  This verification process may take some time,
                  so please be patient and do not close this screen
                </p>
                <p className="mt-3 text-sm font-semibold text-red-400">
                  Thank you for your patience.
                </p>
              </>
            ) : (
              <>
                <p className="leading-relaxed">
                  We will recheck the report, and if any{" "}
                  <span className="font-semibold text-red-400">edited values</span> have been updated,
                  we will also update them in the report.
                </p>
                {safeIntent === "generate" && (
                  <p className="mt-4 leading-relaxed">
                    Generate a new report document for{" "}
                    <span className="font-semibold text-red-400">{name}</span>?
                  </p>
                )}
                {safeIntent === "view" && (
                  <p className="mt-4 leading-relaxed">
                    Open <span className="font-semibold text-red-400">{name}</span> in a new browser
                    tab?
                  </p>
                )}
                {safeIntent === "download" && (
                  <p className="mt-4 leading-relaxed">
                    Download <span className="font-semibold text-red-400">{name}</span> to your device?
                  </p>
                )}
              </>
            )}
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 rounded-xl"
              disabled={phase === "processing"}
            >
              Cancel
            </Button>
            <Button
              onClick={onConfirm}
              className="flex-1 rounded-xl"
              disabled={phase === "processing"}
            >
              {phase === "processing" ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {safeIntent === "download" ? "Preparing…" : "Working…"}
                </span>
              ) : (
                PRIMARY_LABELS[safeIntent]
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateReportDialog;
